import { Http } from './http';
import { Address, User } from '@types';

class UserHttp extends Http {
  prefix = 'user';

  async loginAdmin(userId: number): Promise<string> {
    const res = await this.get<string>(`/${userId}/login-admin`);

    return res.data;
  }

  async updatePartially(userId: number, payload: Record<string, any>): Promise<User> {
    const { data: user } = await this.put<User>(`/${userId}/partial`, payload);

    return user;
  }

  async updateIsCompany(userId: number, isCompany: boolean): Promise<User> {
    return await this.updatePartially(userId, { isCompany });
  }

  async updateAddressById(params: UserAddressUpdateById) {
    const res = await this.put(`/update-address/${params.location.id}`, params);

    return res.data;
  }

  async updateBulk(params: UpdateBulkUsersParams): Promise<number> {
    const { data } = await this.put<number>('/update-bulk', params);

    return data;
  }

  async addAddress(params: UserAddressAdd) {
    const res = await this.post('/add-address', params);

    return res.data;
  }

  async removeAddress(addressId: number) {
    const res = await this.delete(`/remove-address/${addressId}`);

    return res.data;
  }

  async getUserSmsCode(userId: number): Promise<string> {
    const res = await this.get<string>(`/login-code/${userId}`);

    return res.data;
  }

  async reminderSet(params: ReminderSetDto): Promise<boolean> {
    const res = await this.put<boolean>(`/reminder-set/${params.userId}`, params);

    return res.data;
  }

  async reminderUnset(params: ReminderUnsetDto): Promise<boolean> {
    const res = await this.put<boolean>(`/reminder-unset/${params.userId}`, params);

    return res.data;
  }

  async reminderStatus({ userId, creditId }: ReminderUnsetDto): Promise<boolean> {
    const url = creditId ? `/reminder-status/${userId}/${creditId}` : `/reminder-status/${userId}`;
    const res = await this.get<boolean>(url);

    return res.data;
  }

  async inactivateByLogins() {
    const res = await this.get<string>('/inactivate-by-logins');

    return res.data;
  }

  async addBulkActivation(ids: number[], activationId: number) {
    const res = await this.post<void>('/add-bulk-activation', { ids, activationId });

    return res.data;
  }

  async removeActivation(id: number) {
    const res = await this.patch<void>(`/${id}/remove-activation`);

    return res.data;
  }

  async getAll(query: string, count = 0) {
    const res = await this.get<User[]>(`/?_start=0&_end=${count}&q=${query}&_sort=fullName`);

    return res.data;
  }

  async getSources(): Promise<UserChannel[]> {
    const res = await this.get<UserChannel[]>('/sources');

    return res.data;
  }

  async resendEmailActivateLink(userId: number): Promise<number> {
    const res = await this.post<number>(`/resend-email-activate-link/${userId}`);

    return res.data;
  }

  async getProfileProgress(userId: number): Promise<IProfileProgress> {
    const res = await this.get<IProfileProgress>(`/${userId}/profile-progress`);

    return res.data;
  }

  async userCheckByPhone(phone: string): Promise<User> {
    const res = await this.post<User>('/check-by-phone', { phone });

    return res.data;
  }

  async userCheckByEmail(email: string): Promise<User> {
    const res = await this.post<User>('/check-by-email', { email });

    return res.data;
  }

  async getUuidById(id: number): Promise<string> {
    const res = await this.get<string>(`/${id}/uuid`);
    
    return res.data;
  }

  async checkRfc(dto: CheckRfcDto): Promise<CheckRfcRequest> {
    const res = await this.post<CheckRfcRequest>('/check-rfc', dto);

    return res.data;
  }
}

export const userHttp = new UserHttp();

export interface CheckRfcRequest {
  rfcIsFree: boolean;
  userCodes: string[];
}

export interface IProfileProgress {
  profileProgress: 'complete' | 'incomplete';
  missingFields: string[];
}

interface UpdateBulkUsersParams {
  userIds: number[];
  cropIds?: number[];
  stateId?: number;
  managerId?: number;
  activationId?: number;
  gender?: string;
  marriage?: string;
  inactivityReasonId?: number;
  userStatus?: string;
  adminNote?: string;
  reminderDate?: string;
  reminderTime?: string;
  channelAction?: string;
  ownerId?: number;
}

interface UserAddressAdd {
  userId: number;
  location: Partial<Address>;
}

interface UserAddressUpdateById {
  location: Partial<Address>;
}

export interface ReminderSetDto {
  userId: number;
  creditId?: number;
  comment: string;
  contactNextCycle: string | null;
}

export interface ReminderUnsetDto {
  userId: number;
  creditId?: number;
}

export interface UserChannel {
  id: string;
  name: string;
}

export interface CheckRfcDto {
  userId?: number;
  rfc: string;
  isCompany: boolean;
}
